import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import UserDropdown from "./UserDropdown";
import InstallAppButton from "./InstallAppButton";
import PushSoftPrompt from "./PushSoftPrompt";

// Declarative primary-nav catalog. Each entry names the permission key
// that gates its visibility (see `backend/core/permissions.py` for the
// role-mapping and `GET /api/auth/permissions` for the runtime signal).
// `variant` selects a per-role label/URL override so the same `/`
// route can render "Dashboard" / "All Expenses" / "My Expenses" for
// admin/accounting/worker respectively without a role branch here.
function primaryNavCatalog(user, repairsBadge, shopCreditsBadge) {
  const c = user?.counters || {};
  const repairsCount = repairsBadge?.needs_attention || 0;
  const role = user?.role;
  // Root-route label + URL varies by role — everyone shares `/` but the
  // page underneath renders different data depending on the role guard.
  let rootEntry;
  if (role === "admin") {
    rootEntry = { key: "nav.dashboard", to: "/", end: true, label: "Dashboard" };
  } else if (role === "accounting") {
    rootEntry = { key: "nav.expenses",  to: "/", end: true, label: "All Expenses" };
  } else {
    rootEntry = { key: "nav.expenses",  to: "/", end: true, label: "My Expenses" };
  }
  // Worker sub-list of own expenses lives on /expenses under a different
  // label. Admin/accounting reach the same "All Expenses" view via /admin
  // (bookmark card) — it's no longer on the primary nav for them.
  const workerAllMyEntries = role === "worker"
    ? { key: "nav.expenses", to: "/expenses", label: "All My Entries" }
    : null;

  return [
    rootEntry,
    workerAllMyEntries,
    { key: "nav.statements",         to: "/statements",        label: "Statements",       badge: c.open_chases || 0 },
    { key: "nav.driver_charges",     to: "/driver-charges",    label: "Driver Charges",   badge: c.pending_driver_charges || 0 },
    { key: "nav.lessee_charges",     to: "/lessee-charges",    label: "Lessee Charges",   badge: c.pending_lessee_charges || 0 },
    { key: "nav.equipment_repairs",  to: "/equipment-repairs", label: "Equipment Repairs", badge: repairsCount },
    { key: "nav.shop_credits",       to: "/shop-credits",       label: "Shop Credits",     badge: shopCreditsBadge?.drafts || 0 },
    { key: "nav.reports",            to: "/reports",           label: "Reports" },
    { key: "nav.inbox",              to: "/inbox",             label: "Inbox",            badge: c.open_comment_requests || 0 },
  ].filter(Boolean);
}


function NavBadge({ value }) {
  if (!value) return null;
  return (
    <span
      data-testid="nav-badge"
      className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold leading-none text-white"
    >
      {value}
    </span>
  );
}

export default function Layout() {
  const { user, hasPerm } = useAuth();
  const [open, setOpen] = useState(false);
  const [repairsBadge, setRepairsBadge] = useState(null);
  const [shopCreditsBadge, setShopCreditsBadge] = useState(null);

  // Repairs nav badge — poll every 60s. Listens to a lightweight window
  // event for optimistic refresh after a commit inside /equipment-repairs.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const load = () => {
      api.get("/equipment-repairs/badge-counts")
        .then((r) => { if (!cancelled) setRepairsBadge(r.data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    const onRefresh = () => load();
    window.addEventListener("repairs:refresh-badge", onRefresh);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("repairs:refresh-badge", onRefresh); };
  }, [user]);

  // Shop-credits nav badge — same pattern, listens to `shop-credits:refresh-badge`.
  useEffect(() => {
    if (!user) return undefined;
    if (!hasPerm("nav.shop_credits")) return undefined;
    let cancelled = false;
    const load = () => {
      api.get("/shop-credits/counts")
        .then((r) => { if (!cancelled) setShopCreditsBadge(r.data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    const onRefresh = () => load();
    window.addEventListener("shop-credits:refresh-badge", onRefresh);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("shop-credits:refresh-badge", onRefresh); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;
  const items = primaryNavCatalog(user, repairsBadge, shopCreditsBadge).filter((it) => hasPerm(it.key));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link" aria-label="ExpenseReconcile home">
            <img
              src="/logo.png"
              alt="ExpenseReconcile"
              className="h-10 md:h-11 w-auto"
            />
          </Link>
          <nav className="hidden items-center gap-1 md:flex" data-testid="desktop-nav">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-100 text-[#0F1E3D]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}>
                {it.label}<NavBadge value={it.badge} />
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <InstallAppButton variant="header" />
            <UserDropdown />
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {open && (
          <div className="border-t border-slate-200 md:hidden" data-testid="mobile-nav">
            <div className="mx-auto flex max-w-7xl flex-col px-4 py-2 sm:px-6 lg:px-8">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2 text-sm font-medium ${
                      isActive ? "bg-slate-100 text-[#0F1E3D]" : "text-slate-600 hover:bg-slate-50"
                    }`}>
                  {it.label}<NavBadge value={it.badge} />
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <PushSoftPrompt />
        <Outlet />
      </main>
    </div>
  );
}
